import {
    ILandingGeometry,
    IRailingGeometry,
    IRoomGeometry,
    IStairCalculationResult,
    IStairConfig,
    IStairGeometry,
    IStepGeometry,
    IStringerGeometry,
    ISupportPosts,
    IValidationItem,
} from '@stair-craft/common/util';

import { IStairNorms, RESIDENTIAL_NORMS } from '../norms';
import { validate, validateRailing, withHeadroomHint } from '../validator';
import { buildLShapeTurnSupports } from './l-shape-build-turn-supports/build-turn-supports';
import { ILShapeLayout, WINDER_ANGLE_DEG, WINDER_INNER_OFFSET, calculateLayout } from './l-shape-calculate-layout/calculate-layout';
import { generateFlight2Steps } from './l-shape-generate-flight2-steps/generate-flight2-steps';
import { generateLShapeRailings } from './l-shape-generate-l-shape-railings/generate-l-shape-railings';
import { generateLandingStep } from './l-shape-generate-landing-step/generate-landing-step';
import { generateStringers } from './l-shape-generate-stringers/generate-stringers';
import { generateUpperFloorRailing } from './l-shape-generate-upper-floor-railing/generate-upper-floor-railing';
import { generateWinderSteps } from './l-shape-generate-winder-steps/generate-winder-steps';
import { evenPitchRailing } from './shared-baluster-count/baluster-count';
import { flightEntryBottom } from './shared-build-turn-supports/turn-support-steps';
import { IHeadroomResult, calculateHeadroom, lowHeadroomSteps } from './shared-calculate-headroom/calculate-headroom';
import { calculateMaterialVolume } from './shared-calculate-material-volume/calculate-material-volume';
import { applyExitRiser } from './shared-exit-riser/exit-riser';
import { generateFlightSteps } from './shared-generate-flight-steps/generate-flight-steps';
import { shareJunctionPosts } from './shared-junction-posts/junction-posts';
import { IPlanPoint } from './shared-plan-geometry/plan-geometry';
import { capRailingPosts } from './shared-post-caps/post-caps';
import { bringPostsToFloor } from './shared-posts-to-floor/posts-to-floor';
import { addRailingPlanks } from './shared-railing-planks/railing-planks';
import { markTurnRisers } from './shared-riser-board/riser-board';
import { settleStairRailings } from './shared-settle-railings/settle-railings';
import { stairPlacement } from './shared-stair-placement/stair-placement';
import { buildTurnSupportPosts } from './shared-turn-support-posts/turn-support-posts';

// ─── Main Calculator ──────────────────────────────────────────

/** Inclusive integer range [from, to] (empty when to < from). */
function rangeInclusive(from: number, to: number): number[] {
    const indices: number[] = [];
    for (let i: number = from; i <= to; i++) {
        indices.push(i);
    }
    return indices;
}

/**
 * Flight 2 exits along X onto the slab only when it ends exactly on the opening's side edge and
 * the room goes on past that edge. Extracted from `calculateLShape` to keep its complexity low.
 */
function slabPastLShapeExit(config: IStairConfig, flight2Run: number): boolean {
    const sw: number = config.stairWidth;
    if (config.turnDirection === 'right') {
        const exitX: number = config.openingX + sw + flight2Run;
        const edgeX: number = config.openingX + config.openingWidth;
        return Math.abs(exitX - edgeX) < 1 && edgeX < config.roomWidth;
    }
    const exitX: number = config.openingX + config.openingWidth - sw - flight2Run;
    return Math.abs(exitX - config.openingX) < 1 && config.openingX > 0;
}

/**
 * Indices of steps to highlight red: insufficient headroom, flight-1/flight-2 steps out
 * of the room in Z or X, or insufficient entry clearance. Extracted from `calculateLShape`.
 */
function computeWarningStepIndices(
    config: IStairConfig,
    placement: IPlanPoint,
    allSteps: IStepGeometry[],
    treadDepth: number,
    lowHeadroom: readonly number[],
    sw: number,
    flight1Count: number,
    turnStepCount: number,
    minX: number,
    maxX: number,
    maxZ: number
): number[] {
    const warningSet: Set<number> = new Set<number>(lowHeadroom);
    const lastStepIdx: number = allSteps.length - 1;

    // Stair world position (the viewport places it the same way)
    const stairX: number = placement.x;
    const stairZ: number = placement.z;
    const worldMinX: number = stairX + minX;
    const worldMaxX: number = stairX + maxX;
    const worldMaxZ: number = stairZ + maxZ;

    // Flight 1 out of bounds (Z direction)
    if (stairZ < 0) {
        rangeInclusive(0, flight1Count - 1).forEach((i: number): void => {
            if (stairZ + i * treadDepth < 0) {
                warningSet.add(allSteps[i].index);
            }
        });
    }
    if (worldMaxZ > config.roomLength) {
        allSteps.forEach((step: IStepGeometry): void => {
            warningSet.add(step.index);
        });
    }

    // Flight 2 out of bounds (X direction)
    if (worldMinX < 0 || worldMaxX > config.roomWidth) {
        rangeInclusive(flight1Count + turnStepCount, lastStepIdx).forEach((i: number): void => {
            warningSet.add(allSteps[i].index);
        });
    }

    // Flight 1 out of bounds (X direction — stair width)
    if (stairX < 0 || stairX + sw > config.roomWidth) {
        rangeInclusive(0, flight1Count - 1).forEach((i: number): void => {
            warningSet.add(allSteps[i].index);
        });
    }

    // Entry clearance: insufficient space to approach flight 1
    if (stairZ >= 0 && stairZ < sw) {
        const lastBadStep: number = Math.min(flight1Count - 1, Math.ceil((sw - stairZ) / treadDepth));
        rangeInclusive(0, lastBadStep).forEach((i: number): void => {
            warningSet.add(allSteps[i].index);
        });
    }

    return [...warningSet];
}

export function calculateLShape(config: IStairConfig): IStairCalculationResult {
    const norms: IStairNorms = RESIDENTIAL_NORMS;
    const totalRise: number = config.floorHeight + config.floorThickness;
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';

    // ─── Layout ────────────────────────────────────────────
    const layout: ILShapeLayout = calculateLayout(config, norms);
    const { stepCount, riserHeight, treadDepth, flight1Count, flight2Count, turnStepCount } = layout;

    const turnStartZ: number = flight1Count * treadDepth;
    const flight1Run: number = turnStartZ;
    const flight2Run: number = flight2Count * treadDepth;
    const flight1Rise: number = flight1Count * riserHeight;
    const flight2Rise: number = flight2Count * riserHeight;

    // ─── Steps ─────────────────────────────────────────────
    const flight1Steps: IStepGeometry[] = generateFlightSteps(flight1Count, riserHeight, treadDepth, config);

    let turnSteps: IStepGeometry[];
    let landings: ILandingGeometry[];
    const turnIndex: number = flight1Count;

    if (config.turnType === 'landing') {
        const landingStep: IStepGeometry = generateLandingStep(flight1Count, riserHeight, turnStartZ, config, turnIndex);
        turnSteps = [landingStep];
        landings = [
            {
                position: { x: 0, y: landingStep.position.y, z: turnStartZ },
                width: sw,
                depth: sw,
                thickness: config.treadThickness,
            },
        ];
    } else {
        turnSteps = generateWinderSteps(flight1Count, riserHeight, turnStartZ, config, turnIndex);
        landings = [];
    }

    const flight2Steps: IStepGeometry[] = generateFlight2Steps(
        flight1Count,
        turnStepCount,
        flight2Count,
        riserHeight,
        treadDepth,
        turnStartZ,
        config,
        turnIndex + turnStepCount
    );

    const allSteps: IStepGeometry[] = [...flight1Steps, ...turnSteps, ...flight2Steps];
    markTurnRisers(allSteps);
    applyExitRiser(flight2Steps.at(-1), config, slabPastLShapeExit(config, flight2Run));

    // ─── Stringers ─────────────────────────────────────────
    const stringers: IStringerGeometry[] = generateStringers(flight1Steps, flight2Steps, config, riserHeight, turnStartZ);

    // Landing / winder boards — blocks under the outer edges, cut on the riser lines.
    stringers.push(
        ...buildLShapeTurnSupports(config, turnSteps, riserHeight, treadDepth, turnStartZ, flightEntryBottom(stringers, turnSteps))
    );

    // ─── Railings ──────────────────────────────────────────
    // One baluster pitch on the flights and the level runs: the going cut into gaps within the norm.
    const railed: IStairConfig = { ...config, railing: evenPitchRailing(config.railing, treadDepth) };
    const stairRailings: IRailingGeometry[] = config.railing.enabled
        ? generateLShapeRailings(flight1Steps, turnSteps, flight2Steps, railed, turnStartZ)
        : [];
    const upperRailings: IRailingGeometry[] =
        config.railing.enabled && config.railing.upperEnabled ? generateUpperFloorRailing(railed) : [];
    // The support posts stand first: the railing settles around them. Where the stair stands in
    // the room tells which outer corners of a winder turn rest on a wall.
    const placement: IPlanPoint = stairPlacement(config, config, flight1Run + sw);
    const supports: ISupportPosts | undefined = buildTurnSupportPosts(config, turnSteps, turnStartZ, stairRailings, stringers, placement);
    settleStairRailings(stairRailings, allSteps, config.stringerType, config.railing.balusterSpacing, supports);
    bringPostsToFloor(stairRailings, allSteps, stringers);
    shareJunctionPosts(stairRailings, upperRailings, placement, config.railing.balusterSpacing);
    const railings: IRailingGeometry[] = [...stairRailings, ...upperRailings];

    // ─── Bounding box ──────────────────────────────────────
    let minX: number = 0;
    let maxX: number = sw;
    const maxZ: number = turnStartZ + sw;

    if (isRight) {
        maxX = Math.max(sw, sw + flight2Run);
    } else {
        minX = Math.min(0, -flight2Run);
    }

    const geometry: IStairGeometry = {
        steps: allSteps,
        risers: config.risersEnabled,
        boundingBox: {
            min: { x: minX, y: 0, z: 0 },
            max: { x: maxX, y: totalRise, z: maxZ },
        },
        stringers,
        railings,
        landings,
        supports,
    };

    // ─── Derived values ────────────────────────────────────
    // totalRun = Z-axis footprint (used for viewport positioning & opening alignment)
    const totalRun: number = flight1Run + sw;
    const stairAngle: number = Math.atan2(riserHeight, treadDepth) * (180 / Math.PI);
    const flight1MarchLen: number = Math.sqrt(flight1Rise ** 2 + flight1Run ** 2);
    const flight2MarchLen: number = Math.sqrt(flight2Rise ** 2 + flight2Run ** 2);
    const totalMarchLength: number = flight1MarchLen + flight2MarchLen;
    addRailingPlanks(railings, stringers, allSteps, config, placement, supports);
    capRailingPosts(railings);
    const headroomResult: IHeadroomResult = calculateHeadroom(allSteps, config, placement);
    const headroom: number = headroomResult.headroom;
    const blondelValue: number = 2 * riserHeight + treadDepth;

    // ─── Material estimation ───────────────────────────────
    const { woodVolumeM3, estimatedWeightKg, estimatedCostUsd } = calculateMaterialVolume(
        allSteps,
        stepCount,
        riserHeight,
        flight1MarchLen,
        flight2MarchLen,
        railings,
        config,
        supports
    );

    // ─── Room geometry ─────────────────────────────────────
    const room: IRoomGeometry = {
        roomLength: config.roomLength,
        roomWidth: config.roomWidth,
        floorHeight: config.floorHeight,
        floorThickness: config.floorThickness,
        openingLength: config.openingLength,
        openingWidth: config.openingWidth,
        openingX: config.openingX,
        openingZ: config.openingZ,
    };

    // ─── Validation ────────────────────────────────────────
    const validation: IValidationItem[] = withHeadroomHint(validate(riserHeight, treadDepth, stairAngle, headroom, sw), false);
    validation.push(...validateRailing(config.railing));

    if (config.turnType === 'winder') {
        // СП 54.13330 (former СНиП 2.08.01-89): winder depth is measured norms.winderMeasureOffset from the narrow
        // (inner) edge. The inner edge sits at radius WINDER_INNER_OFFSET from the pivot, so the depth is the chord
        // of one winder sector at radius WINDER_INNER_OFFSET + norms.winderMeasureOffset.
        const measureRadius: number = WINDER_INNER_OFFSET + norms.winderMeasureOffset;
        const winderHalfAngleRad: number = (WINDER_ANGLE_DEG * Math.PI) / 180 / 2;
        const actualInnerDepth: number = Math.round(2 * measureRadius * Math.sin(winderHalfAngleRad));

        const depthLabel: string = $localize`:@@valWinderInnerDepth:Глубина забежной у узкого края`;
        const unitMm: string = $localize`:@@unitMm:мм`;
        const unitMin: string = $localize`:@@unitMin:мин`;
        validation.push({
            code: 'WINDER_INNER_DEPTH',
            severity: actualInnerDepth >= norms.winderMinInnerDepth ? 'ok' : 'error',
            value: actualInnerDepth,
            min: norms.winderMinInnerDepth,
            max: null,
            message: `${depthLabel}: ${String(actualInnerDepth)}${unitMm} (${unitMin} ${String(norms.winderMinInnerDepth)})`,
        });
    }

    const isValid: boolean = validation.every((v: IValidationItem) => v.severity !== 'error');

    // ─── Step warnings (red highlight) ─────────────────────
    const warningStepIndices: number[] = computeWarningStepIndices(
        config,
        placement,
        allSteps,
        treadDepth,
        lowHeadroomSteps(headroomResult, norms.headroomMin),
        sw,
        flight1Count,
        turnStepCount,
        minX,
        maxX,
        maxZ
    );

    return {
        riserHeight: Math.round(riserHeight * 10) / 10,
        treadDepth: Math.round(treadDepth * 10) / 10,
        stairAngle: Math.round(stairAngle),
        totalRun: Math.round(totalRun),
        headroom: Math.round(headroom),
        blondelValue: Math.round(blondelValue),
        totalMarchLength: Math.round(totalMarchLength),
        woodVolumeM3: Math.round(woodVolumeM3 * 1000) / 1000,
        estimatedWeightKg: Math.round(estimatedWeightKg),
        estimatedCostUsd: Math.round(estimatedCostUsd),
        placementValid: warningStepIndices.length === 0,
        stepCount,
        flight1Count,
        flight2Count,
        turnStepCount,
        room,
        geometry,
        validation,
        isValid,
        warningStepIndices,
    };
}
